import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Pin, ClipboardList, Target, BarChart3, Loader2, Edit2, Trash2 } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';
import type { Project } from '../types/cowork';

interface ProjectDetailPageProps {
  projectId: string;
  onBack: () => void;
  onOpenTasks: () => void;
  onOpenOkrs: () => void;
  onOpenDashboards: () => void;
}

interface DetailData {
  project: Project;
  tasks: Array<Record<string, unknown>>;
  okrs: Array<Record<string, unknown>>;
  dashboards: Array<Record<string, unknown>>;
}

export function ProjectDetailPage({
  projectId, onBack, onOpenTasks, onOpenOkrs, onOpenDashboards,
}: ProjectDetailPageProps) {
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    coworkApi.projects.get(projectId)
      .then((d) => { setData(d); setEditTitle(d.project.title); setEditDescription(d.project.description); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-full text-[#94a3b8]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  const { project, tasks, okrs, dashboards } = data;
  const objectives = okrs.filter((o) => o.type === 'objective');
  const keyResults = okrs.filter((o) => o.type === 'key_result');
  const taskCounts = tasks.reduce<Record<string, number>>((acc, t) => {
    const status = String(t.status ?? 'queue');
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#eaecf0]">
        <button onClick={onBack} className="text-[12px] text-[#667085] hover:text-[#101828] flex items-center gap-1.5 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" />
          Projects
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="text-[36px] leading-none">{project.emoji || '📁'}</div>
            <div className="flex-1 min-w-0">
              {editing ? (
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-2 py-1 text-[20px] font-semibold border border-[#d0d5dd] rounded-md focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40"
                />
              ) : (
                <h1 className="text-[20px] font-semibold text-[#101828]">{project.title}</h1>
              )}
              {editing ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  placeholder="Description"
                  className="mt-2 w-full px-2 py-1 text-[13px] border border-[#d0d5dd] rounded-md focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 resize-none"
                />
              ) : (
                project.description && <p className="text-[13px] text-[#667085] mt-1 max-w-2xl">{project.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={async () => {
                await coworkApi.projects.pin(project.id, !project.pinned).catch((e) => alert(e.message));
                refresh();
              }}
              className={`p-2 rounded-lg border transition-colors ${
                project.pinned
                  ? 'bg-[#fef3c7] border-[#fde68a] text-[#d97706]'
                  : 'border-[#d0d5dd] text-[#667085] hover:bg-[#f9fafb]'
              }`}
              title={project.pinned ? 'Unpin' : 'Pin to sidebar'}
            >
              <Pin className={`w-3.5 h-3.5 ${project.pinned ? 'fill-current' : ''}`} />
            </button>
            {editing ? (
              <>
                <button
                  onClick={async () => {
                    await coworkApi.projects.update(project.id, { title: editTitle, description: editDescription });
                    setEditing(false);
                    refresh();
                  }}
                  className="px-3 py-1.5 rounded-lg bg-[#be43ff] text-white text-[12px] hover:bg-[#a835e0]"
                  style={{ fontWeight: 600 }}
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditing(false); setEditTitle(project.title); setEditDescription(project.description); }}
                  className="px-3 py-1.5 rounded-lg border border-[#d0d5dd] text-[12px] text-[#667085]"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="p-2 rounded-lg border border-[#d0d5dd] text-[#667085] hover:bg-[#f9fafb]"
                title="Edit"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={async () => {
                if (!confirm('Delete this project? Its tasks, OKRs, and dashboards stay in their global views (just unlinked).')) return;
                await coworkApi.projects.delete(project.id);
                onBack();
              }}
              className="p-2 rounded-lg border border-[#d0d5dd] text-[#94a3b8] hover:text-[#ef4444] hover:border-[#fecaca]"
              title="Delete project"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tasks */}
        <Section title="Tasks" icon={<ClipboardList className="w-4 h-4" />} count={tasks.length} onOpenAll={onOpenTasks}>
          {tasks.length === 0 ? (
            <Empty text="No tasks yet" />
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {(['queue', 'working', 'review', 'done'] as const).map((s) => (
                  <div key={s} className="rounded-lg bg-[#f8fafc] border border-[#eaecf0] px-2.5 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">{s}</div>
                    <div className="text-[18px] font-semibold text-[#101828] mt-0.5">{taskCounts[s] ?? 0}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                {tasks.slice(0, 6).map((t) => (
                  <div key={t.id as string} className="flex items-center justify-between gap-2 rounded-md hover:bg-[#f8fafc] px-2 py-1.5">
                    <span className="text-[12px] text-[#101828] truncate flex-1">{t.title as string}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#94a3b8]">{t.status as string}</span>
                  </div>
                ))}
                {tasks.length > 6 && (
                  <button onClick={onOpenTasks} className="text-[11px] text-[#be43ff] hover:underline px-2 mt-1">View all {tasks.length} tasks →</button>
                )}
              </div>
            </>
          )}
        </Section>

        {/* OKRs */}
        <Section title="OKRs" icon={<Target className="w-4 h-4" />} count={okrs.length} onOpenAll={onOpenOkrs}>
          {okrs.length === 0 ? (
            <Empty text="No OKRs yet" />
          ) : (
            <div className="space-y-3">
              {objectives.map((o) => {
                const objKrs = keyResults.filter((kr) => kr.parent_id === o.id);
                return (
                  <div key={o.id as string} className="rounded-lg border border-[#eaecf0] p-3">
                    <div className="text-[13px] font-semibold text-[#101828]">{o.title as string}</div>
                    {objKrs.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {objKrs.map((kr) => (
                          <li key={kr.id as string} className="text-[11px] text-[#475569] flex justify-between gap-2">
                            <span className="truncate">{kr.title as string}</span>
                            <span className="text-[#94a3b8] shrink-0">{Math.round((kr.progress as number || 0) * 100)}%</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Dashboards */}
        <Section title="Dashboards" icon={<BarChart3 className="w-4 h-4" />} count={dashboards.length} onOpenAll={onOpenDashboards}>
          {dashboards.length === 0 ? (
            <Empty text="No dashboards scoped to this project yet" />
          ) : (
            <div className="space-y-1.5">
              {dashboards.map((d) => (
                <div key={d.id as string} className="rounded-md hover:bg-[#f8fafc] px-2 py-1.5 text-[12px] text-[#101828]">
                  {d.title as string}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Agents */}
        <Section title="Agents on this project" icon={<ClipboardList className="w-4 h-4" />} count={project.assignedAgentIds.length}>
          {project.assignedAgentIds.length === 0 ? (
            <Empty text="Agents are auto-assigned per task. Add explicit assignments in the project settings." />
          ) : (
            <div className="space-y-1.5">
              {project.assignedAgentIds.map((id) => (
                <div key={id} className="text-[12px] text-[#475569] px-2 py-1">{id}</div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({
  title, icon, count, onOpenAll, children,
}: {
  title: string; icon: React.ReactNode; count: number;
  onOpenAll?: () => void; children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#eaecf0] bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#101828]">
          <span className="text-[#64748b]">{icon}</span>
          <span className="text-[13px] font-semibold">{title}</span>
          <span className="text-[11px] text-[#94a3b8]">{count}</span>
        </div>
        {onOpenAll && (
          <button onClick={onOpenAll} className="text-[11px] text-[#be43ff] hover:underline">Open in main view →</button>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="text-[12px] text-[#94a3b8] py-3">{text}</div>;
}
